import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiRefreshCw, FiSlash } from "react-icons/fi";
import { IoSearchOutline } from "react-icons/io5";
import MasterFormPage from "../common/MasterFormPage";
import MasterShell from "../common/MasterShell";
import MasterToast from "../common/MasterToast";
import StatusBadge from "../common/StatusBadge";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import {
  createMasterRecord,
  deactivateMasterRecord,
  getMasterDomainItems,
  getMasterRecordTranslations,
  MASTER_DOMAIN_OPTIONS,
  updateMasterRecord,
} from "../../services/masterDataService";
import "../common/new_MasterList.css";
import "../../components/report/HierarchyFilterBar.css";
import "../../components/report/new_HierarchyFilterBar.css";
import "./References.css";

const LANGUAGE_OPTIONS = [
  { label: "English", value: "en" },
  { label: "Gujarati", value: "gu" },
  { label: "Hindi", value: "hi" },
  { label: "Marathi", value: "mr" },
];

const normalizeText = (value) => String(value ?? "").trim();

const createInitialFormValues = () => ({
  aliases_text: "",
  code: "",
  is_active: "1",
  label_en: "",
  label_gu: "",
  label_hi: "",
  label_mr: "",
  sort_order: "",
  states_text: "",
});

const getInitialLanguage = () => {
  const storedLanguage = normalizeText(localStorage.getItem("selectedLanguage"));
  const preferredLanguage = normalizeText(
    localStorage.getItem("preferred_language")
  );
  const languageMap = {
    english: "en",
    en: "en",
    gujarati: "gu",
    gu: "gu",
    hindi: "hi",
    hi: "hi",
    marathi: "mr",
    mr: "mr",
  };

  return (
    languageMap[storedLanguage.toLowerCase()] ||
    languageMap[preferredLanguage.toLowerCase()] ||
    "en"
  );
};

const getInitialState = () =>
  normalizeText(localStorage.getItem("userState")) ||
  normalizeText(localStorage.getItem("state")) ||
  "Gujarat";

const parseCommaSeparatedValues = (value) =>
  Array.from(
    new Set(
      normalizeText(value)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    )
  );

const formatDomainLabel = (value) =>
  MASTER_DOMAIN_OPTIONS.find((option) => option.value === value)?.label || value;

const mapRecordToFormValues = (record) => ({
  aliases_text: "",
  code: record.code || "",
  is_active: record.is_active === false ? "0" : "1",
  label_en: record.labels?.en || "",
  label_gu: record.labels?.gu || "",
  label_hi: record.labels?.hi || "",
  label_mr: record.labels?.mr || "",
  sort_order:
    record.sort_order === null || record.sort_order === undefined
      ? ""
      : String(record.sort_order),
  states_text: Array.isArray(record.states) ? record.states.join(", ") : "",
});

const buildLabelPayload = (formValues) => ({
  en: normalizeText(formValues.label_en),
  gu: normalizeText(formValues.label_gu),
  hi: normalizeText(formValues.label_hi),
  mr: normalizeText(formValues.label_mr),
});

export default function LawReferencePage() {
  const { canAny, user } = useCapabilities();
  const canManageMasters = canAny("masters", ["manage"]);
  const [errors, setErrors] = useState({});
  const [formValues, setFormValues] = useState(createInitialFormValues);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreparingForm, setIsPreparingForm] = useState(false);
  const [mode, setMode] = useState("list");
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("case_property_type");
  const [selectedLang, setSelectedLang] = useState(getInitialLanguage);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedState, setSelectedState] = useState(
    () => normalizeText(user?.state) || getInitialState()
  );
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const [touchedFields, setTouchedFields] = useState({});

  const currentDomainLabel = formatDomainLabel(selectedDomain);
  const effectiveUserState = normalizeText(user?.state) || getInitialState();

  const availableStateOptions = useMemo(() => {
    const options = [{ label: "All States", value: "all" }];

    if (effectiveUserState) {
      options.unshift({
        label: effectiveUserState,
        value: effectiveUserState,
      });
    }

    return options;
  }, [effectiveUserState]);

  const closeToast = useCallback(() => {
    setToast((currentToast) => ({ ...currentToast, open: false }));
  }, []);

  const resetFormState = useCallback(() => {
    setErrors({});
    setFormValues(createInitialFormValues());
    setSelectedRecord(null);
    setTouchedFields({});
  }, []);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getMasterDomainItems(selectedDomain, {
        includeInactive,
        lang: selectedLang,
        state: selectedState,
      });

      setRecords(response.items || []);
    } catch (error) {
      setRecords([]);
      setToast({
        message: error?.message || "Failed to load master data.",
        open: true,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive, selectedDomain, selectedLang, selectedState]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (!selectedState && effectiveUserState) {
      setSelectedState(effectiveUserState);
    }
  }, [effectiveUserState, selectedState]);

  const filteredRecords = useMemo(() => {
    const normalizedSearch = normalizeText(searchTerm).toLowerCase();

    if (!normalizedSearch) {
      return records;
    }

    return records.filter((record) =>
      [record.code, record.label, record.states?.join(", ")]
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [records, searchTerm]);

  const handleStartCreate = () => {
    resetFormState();
    setMode("create");
  };

  const handleStartEdit = useCallback(
    async (record) => {
      setErrors({});
      setIsPreparingForm(true);

      try {
        const masterRecord = await getMasterRecordTranslations(
          selectedDomain,
          record.id,
          {
            includeInactive: true,
            state: selectedState,
          }
        );

        setSelectedRecord(masterRecord);
        setFormValues(mapRecordToFormValues(masterRecord));
        setTouchedFields({});
        setMode("edit");
      } catch (error) {
        setToast({
          message: error?.message || "Failed to load master value details.",
          open: true,
          severity: "error",
        });
      } finally {
        setIsPreparingForm(false);
      }
    },
    [selectedDomain, selectedState]
  );

  const handleBackToList = () => {
    resetFormState();
    setMode("list");
  };

  const validateForm = () => {
    const nextErrors = {};

    if (mode === "create") {
      if (!normalizeText(formValues.code)) {
        nextErrors.code = "Code is required.";
      } else if (!/^[A-Z0-9_]+$/.test(normalizeText(formValues.code))) {
        nextErrors.code = "Use only A-Z, 0-9, and underscore.";
      }

      if (!normalizeText(formValues.label_en)) {
        nextErrors.label_en = "English label is required.";
      }
    }

    if (!normalizeText(formValues.sort_order)) {
      nextErrors.sort_order = "Sort order is required.";
    } else if (!Number.isFinite(Number(formValues.sort_order))) {
      nextErrors.sort_order = "Sort order must be a valid number.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setToast({
        message: "Please fix the highlighted fields before saving.",
        open: true,
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
    setTouchedFields((currentFields) => ({
      ...currentFields,
      [name]: true,
    }));
  };

  const buildCreatePayload = () => ({
    aliases: parseCommaSeparatedValues(formValues.aliases_text),
    code: normalizeText(formValues.code),
    labels: buildLabelPayload(formValues),
    sort_order: Number(formValues.sort_order),
    states: parseCommaSeparatedValues(formValues.states_text),
  });

  const buildUpdatePayload = () => {
    const payload = {
      is_active: formValues.is_active === "1",
      labels: buildLabelPayload(formValues),
      sort_order: Number(formValues.sort_order),
      states: parseCommaSeparatedValues(formValues.states_text),
    };

    if (touchedFields.aliases_text) {
      payload.aliases = parseCommaSeparatedValues(formValues.aliases_text);
    }

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (mode === "create") {
        const response = await createMasterRecord(
          selectedDomain,
          buildCreatePayload()
        );

        setToast({
          message: response.message,
          open: true,
          severity: "success",
        });
      } else if (selectedRecord) {
        const response = await updateMasterRecord(
          selectedDomain,
          selectedRecord.id,
          buildUpdatePayload()
        );

        setToast({
          message: response.message,
          open: true,
          severity: "success",
        });
      }

      handleBackToList();
      await loadRecords();
    } catch (error) {
      setToast({
        message: error?.message || "Failed to save master value.",
        open: true,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (record) => {
    if (!window.confirm(`Deactivate ${record.code}?`)) {
      return;
    }

    try {
      const response = await deactivateMasterRecord(selectedDomain, record.id);
      setToast({
        message: response.message,
        open: true,
        severity: "success",
      });
      await loadRecords();
    } catch (error) {
      setToast({
        message: error?.message || "Failed to deactivate master value.",
        open: true,
        severity: "error",
      });
    }
  };

  const fields = useMemo(
    () => [
      {
        name: "code",
        label: "Code",
        placeholder: "Enter code like DRUGS_NARCOTICS",
        required: mode === "create",
        error: errors.code,
        disabled: mode === "edit",
      },
      {
        name: "label_en",
        label: "English Label",
        placeholder: "Enter English label",
        required: mode === "create",
        error: errors.label_en,
        hint:
          mode === "edit"
            ? "Update this only if you want to change the English label."
            : undefined,
      },
      {
        name: "label_gu",
        label: "Gujarati Label",
        placeholder: "Enter Gujarati label",
      },
      {
        name: "label_hi",
        label: "Hindi Label",
        placeholder: "Enter Hindi label",
      },
      {
        name: "label_mr",
        label: "Marathi Label",
        placeholder: "Enter Marathi label",
      },
      {
        name: "sort_order",
        label: "Sort Order",
        placeholder: "Enter sort order",
        required: true,
        type: "number",
        error: errors.sort_order,
      },
      {
        name: "states_text",
        label: "State Scope",
        placeholder: "Leave blank for global or enter states separated by commas",
        fullWidth: true,
        hint: "Example: Gujarat, Maharashtra. Leave blank to keep the entry global.",
      },
      {
        name: "aliases_text",
        label: "Aliases",
        placeholder: "Enter aliases separated by commas",
        fullWidth: true,
        hint:
          mode === "edit"
            ? "Aliases are only sent if you change this field."
            : "Optional. Add legacy display strings separated by commas.",
      },
      {
        name: "is_active",
        label: "Status",
        placeholder: "Select status",
        type: "select",
        hidden: mode !== "edit",
        options: [
          { label: "Active", value: "1" },
          { label: "Inactive", value: "0" },
        ],
      },
    ],
    [errors.code, errors.label_en, errors.sort_order, mode]
  );

  if (mode !== "list") {
    return (
      <MasterShell pageClassName="references-module">
        <MasterFormPage
          fields={fields}
          onBack={handleBackToList}
          onChange={handleChange}
          onSubmit={handleSubmit}
          submitLabel={mode === "create" ? "Save Master Value" : "Update Master Value"}
          submitting={submitting}
          title={mode === "create" ? `Add ${currentDomainLabel}` : `Edit ${currentDomainLabel}`}
          values={formValues}
        >
        </MasterFormPage>

        <MasterToast
          message={toast.message}
          onClose={closeToast}
          open={toast.open}
          severity={toast.severity}
        />
      </MasterShell>
    );
  }

  const loadingTitle = isPreparingForm ? "Preparing master form" : "Loading master data";
  const loadingText = isPreparingForm
    ? "Fetching the selected master value and available translations."
    : "Please wait while the selected domain is being prepared.";

  return (
    <MasterShell pageClassName="references-module">
      <div className="masters-card new_master-records-list references-module__list-card">
        <div className="references-module__tabs">
          {MASTER_DOMAIN_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`references-module__tab${
                option.value === selectedDomain ? " references-module__tab--active" : ""
              }`}
              onClick={() => {
                setSelectedDomain(option.value);
                setSearchTerm("");
              }}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="masters-card__header masters-card__header--list references-module__toolbar">
          <div className="masters-list__toolbar masters-list__toolbar--header">
            <div className="masters-search references-module__search">
              <IoSearchOutline className="masters-search__icon" />
              <input
                className="masters-search__input"
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={`Search ${currentDomainLabel}`}
                type="text"
                value={searchTerm}
              />
            </div>

            <div className="masters-list__toolbar-inline-content">
              <div className="report-filter-bar report-filter-bar--compact new_report-filter-bar references-module__filters">
                <div className="report-filter-grid">
                  <div className="report-filter-field">
                    <label className="report-filter-label" htmlFor="master-lang">
                      Language
                    </label>
                    <select
                      className="report-filter-control"
                      id="master-lang"
                      onChange={(event) => setSelectedLang(event.target.value)}
                      value={selectedLang}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="report-filter-field">
                    <label className="report-filter-label" htmlFor="master-state">
                      State
                    </label>
                    <select
                      className="report-filter-control"
                      id="master-state"
                      onChange={(event) => setSelectedState(event.target.value)}
                      value={selectedState}
                    >
                      {availableStateOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="report-filter-field references-module__checkbox-field">
                    <span className="report-filter-label">Status</span>
                    <label className="references-module__checkbox">
                      <input
                        checked={includeInactive}
                        onChange={(event) => setIncludeInactive(event.target.checked)}
                        type="checkbox"
                      />
                      Include inactive
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="masters-card__actions">
              <button
                className="masters-btn masters-btn--secondary"
                disabled={isLoading || isPreparingForm}
                onClick={loadRecords}
                type="button"
              >
                <FiRefreshCw />
                Refresh
              </button>
              {canManageMasters ? (
                <button
                  className="masters-btn"
                  disabled={isPreparingForm}
                  onClick={handleStartCreate}
                  type="button"
                >
                  <FiPlus />
                  Add Value
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {isLoading || isPreparingForm ? (
          <div className="masters-state">
            <div className="masters-state__loader" />
            <h2 className="masters-state__title">{loadingTitle}</h2>
            <p className="masters-state__text">{loadingText}</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="masters-state">
            <h2 className="masters-state__title">No master values found</h2>
            <p className="masters-state__text">
              Try changing the domain or filters, or add a new value.
            </p>
          </div>
        ) : (
          <div className="masters-table-wrap">
            <div className="masters-table-scroll">
              <table className="masters-table">
                <thead>
                  <tr>
                    <th>Sr. No.</th>
                    <th>Code</th>
                    <th>Label</th>
                    <th>Scope</th>
                    <th>Sort Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record, index) => (
                    <tr key={record.id}>
                      <td>{index + 1}</td>
                      <td className="references-module__code">{record.code}</td>
                      <td>{record.label || "-"}</td>
                      <td>
                        {record.states?.length > 0
                          ? record.states.join(", ")
                          : "Global"}
                      </td>
                      <td>{record.sort_order ?? "-"}</td>
                      <td>
                        <StatusBadge value={record.is_active} />
                      </td>
                      <td>
                        <div className="masters-table__actions">
                          {canManageMasters ? (
                            <>
                              <button
                                className="masters-icon-btn masters-icon-btn--edit"
                                disabled={isPreparingForm}
                                onClick={() => handleStartEdit(record)}
                                type="button"
                              >
                                <FiEdit2 />
                              </button>
                              {record.is_active !== false ? (
                                <button
                                  className="masters-icon-btn references-module__deactivate-btn"
                                  disabled={isPreparingForm}
                                  onClick={() => handleDeactivate(record)}
                                  type="button"
                                >
                                  <FiSlash />
                                </button>
                              ) : null}
                            </>
                          ) : (
                            <span className="references-module__action-placeholder">
                              Read only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <MasterToast
        message={toast.message}
        onClose={closeToast}
        open={toast.open}
        severity={toast.severity}
      />
    </MasterShell>
  );
}
